import { Button, Tooltip } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router';

interface Props {
  /** Explicit target. If omitted, falls back to history. */
  to?: string;
  /** Label override. Default: "Back". */
  label?: string;
  /** Use icon-only ghost button when true (default). Set false for labelled button. */
  iconOnly?: boolean;
}

/**
 * Back navigation control surfaced inside <PageHeader>. Honors an explicit
 * `to` prop, otherwise pops history. Hides itself when there's nowhere to go
 * back to (location key === 'default', i.e. direct entry into the app).
 */
export function BackButton({ to, label = 'Back', iconOnly = true }: Props) {
  const navigate = useNavigate();
  const location = useLocation();

  // First entry into the SPA has key='default'; nothing to pop.
  const canPop = location.key !== 'default';
  if (!to && !canPop) return null;

  const handleClick = () => {
    if (to) navigate(to);
    else navigate(-1);
  };

  if (iconOnly) {
    return (
      <Tooltip title={label}>
        <Button
          type="text"
          shape="circle"
          icon={<ArrowLeftOutlined />}
          aria-label={label}
          onClick={handleClick}
        />
      </Tooltip>
    );
  }

  return (
    <Button icon={<ArrowLeftOutlined />} onClick={handleClick}>
      {label}
    </Button>
  );
}
